'use client';

import { useMergeRefs } from '@floating-ui/react';
import { IconMoreFilled } from '@zillow/constellation-icons/react/filled/icon-more-filled';
import { forwardRef, useContext, useMemo } from 'react';

import { IconButton } from '../icon-button';
import { Slot } from '../slot';
import { MenuRootContext } from './context/menu-root.context';
import type { MenuTriggerPropsInterface } from './types/menu-trigger.types';

/**
 * @since 10.0.0
 */
export const MenuTrigger = forwardRef<HTMLButtonElement, MenuTriggerPropsInterface>(
  (props: MenuTriggerPropsInterface, forwardedRef) => {
    const { children, css: cssProp, ...rest } = props;
    const { menu } = useContext(MenuRootContext);
    const mergedRefs = useMergeRefs([menu.refs.setReference, forwardedRef]);

    const commonProps = useMemo(
      () => ({
        'ref': mergedRefs,
        'data-c11n-version': __C11N_VERSION__,
        'data-c11n-component': 'Menu.Trigger',
        ...menu.getReferenceProps(rest),
      }),
      [mergedRefs, menu, rest],
    );

    if (children) {
      return <Slot {...commonProps}>{children}</Slot>;
    }

    return (
      <IconButton
        {...commonProps}
        icon={<IconMoreFilled />}
        emphasis="bare"
        tone="brand"
        title="More"
        css={cssProp}
      />
    );
  },
);

MenuTrigger.displayName = 'Menu.Trigger';
